import React from "react";

import { setIn, useFormikContext } from "formik";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  getOptionAsValue,
  withNamespace,
} from "components/Form/FormikControls";

import { TransactionalSaleTypes } from "constants/sale";

import { productFields } from "lib/products";

import { currentSaleSelector, selectCurrentDeploymentIds } from "selectors";

import { getProductsByDeploymentId } from "selectors/speciesAttributes";
import { Grid, Typography } from "@material-ui/core";
import { snakeCaseProductFormFieldMap } from "./ProductSelectField";

interface DefaultFormFieldMap {
  age_id: string;
  breed_id: string;
  product_id: string;
  sale_round_id: string;
  sex_id: string;
}

interface ProductSelectFieldBulkProps {
  deploymentId?: number;
  disabled?: boolean;
  formFieldMap?: DefaultFormFieldMap;
  label?: string;
  name?: string;
  ns?: string;
}

export const RenderProductOptionWithDetail = (option: {
  id: number;
  isHeading: boolean;
  quickCode: string;
  name: string;
  disabled: boolean;
}) => (
  <Grid component="div" container spacing={2}>
    <Grid item xs={2}>
      <Typography noWrap>
        {option.isHeading ? "Quick Code" : option.quickCode}
      </Typography>
    </Grid>
    <Grid item xs={2}>
      <Typography noWrap>
        {option.isHeading ? "Description" : option.name}
      </Typography>
    </Grid>
  </Grid>
);

export const ProductSelectFieldBulk = React.memo(
  ({
    label,
    deploymentId = null,
    disabled = false,
    formFieldMap = snakeCaseProductFormFieldMap,
    name = "product_id",
    ns = null,
  }: ProductSelectFieldBulkProps): React.JSX.Element => {
    const formik = useFormikContext<{
      product_id: number;
      sale_round_id: number;
    }>();

    const { values, setValues } = formik;

    const defaultDeploymentId = useSelector(selectCurrentDeploymentIds)[0];
    const selectedDeploymentId = deploymentId || defaultDeploymentId;
    const products = useSelector(
      getProductsByDeploymentId(selectedDeploymentId),
    );

    // Build the list of options for the sale type (and round if applicable)
    const currentSale = useSelector(currentSaleSelector) || {};
    const saleType = currentSale.sale_type;
    const productOptions = React.useMemo(
      () =>
        products.filter(
          p =>
            TransactionalSaleTypes.includes(saleType) ||
            p.sale_rounds.includes(values.sale_round_id) ||
            p.sale_rounds.length === 0,
        ),
      [products, values, saleType],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Product ID from select option
    const setProduct = (productId: any) => {
      const product = products.find(p => p.id === productId);
      if (product) {
        let newValues = { ...values, [name]: productId };
        productFields.forEach(field => {
          const newFieldValue = product[field];
          newValues = setIn(
            newValues,
            withNamespace(ns, formFieldMap[field]),
            newFieldValue,
          );
        });
        setValues(newValues);
      }
    };

    const options = [
      {
        disabled: true,
        isHeading: true,
      },
      ...productOptions.map(p => ({
        disabled: false,
        isHeading: false,
        value: p.id,
        quickCode: p.quick_code,
        label: p.quick_code,
        name: p.name,
      })),
    ];

    // Do not allow bulk update with no product, as doing so can be ambiguous.
    // Bulk erasure of sex, breed or age can be done on those fields individually.
    let autocompleteLabel = label;
    if (!productOptions.length)
      autocompleteLabel = "No product options are available.";

    return (
      <Autocomplete
        disableClearable
        requiresInput
        name={name}
        options={options}
        label={autocompleteLabel}
        renderOption={RenderProductOptionWithDetail}
        renderOptionHeadings
        getOptionValue={getOptionAsValue}
        onChangeExtra={setProduct}
        disabled={disabled || !productOptions.length}
      />
    );
  },
);
